import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ExclamationCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import AuthActionShell from '../components/Auth/AuthActionShell';
import { Alert, Button, Spinner } from '../components/common';
import PasswordInput from '../components/common/PasswordInput';
import PasswordPolicyChecklist from '../components/common/PasswordPolicyChecklist';
import { useAccountPasswordValidation } from '../hooks/useAccountPasswordValidation';
import { ACCOUNT_PASSWORD_POLICY_COPY } from '../services/accountSecurity';
import { authService } from '../services/authService';
import { encodePostAuthIntentForQuery } from '../utils/postAuthIntent';

interface ResetPasswordActionProps {
  oobCode: string;
  intentPath?: string | null;
  onTerminalResult?: () => void;
}

const mapResetActionCodeError = (errorCode: string): string => {
  switch (errorCode) {
    case 'auth/expired-action-code':
      return 'Link resetu hasła wygasł. Poproś o nowy link.';
    case 'auth/invalid-action-code':
      return 'Link resetu hasła jest nieprawidłowy lub został już użyty.';
    case 'auth/user-disabled':
      return 'Konto zostało wyłączone. Skontaktuj się z administratorem.';
    case 'auth/weak-password':
      return ACCOUNT_PASSWORD_POLICY_COPY;
    case 'auth/network-request-failed':
      return 'Brak połączenia z siecią. Sprawdź internet i spróbuj ponownie.';
    default:
      return authService.mapFirebaseError(errorCode);
  }
};

const isTerminalResetCodeError = (errorCode: string): boolean => {
  return (
    errorCode === 'auth/expired-action-code' ||
    errorCode === 'auth/invalid-action-code' ||
    errorCode === 'auth/user-disabled' ||
    errorCode === 'auth/user-not-found'
  );
};

const buildIntentQuery = (intentPath?: string | null): string => {
  const encodedIntent = encodePostAuthIntentForQuery(intentPath);
  return encodedIntent ? `?postAuthIntent=${encodedIntent}` : '';
};

const ResetPasswordAction: React.FC<ResetPasswordActionProps> = ({
  oobCode,
  intentPath,
  onTerminalResult,
}) => {
  const [codeLoading, setCodeLoading] = useState(true);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resetEmail, setResetEmail] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const stateHeadingRef = useRef<HTMLHeadingElement>(null);
  const passwordValidation = useAccountPasswordValidation(newPassword);
  const isPasswordMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const canSubmitNewPassword =
    Boolean(newPassword) &&
    passwordValidation.isValid &&
    !isPasswordMismatch &&
    newPassword === confirmPassword &&
    !submitSuccess;
  const intentQuery = buildIntentQuery(intentPath);
  const loginTarget = `/login${intentQuery}`;
  const resetRequestTarget = `/reset-password${intentQuery}`;

  useEffect(() => {
    let isMounted = true;

    const verifyCode = async () => {
      setCodeLoading(true);
      setCodeError(null);
      try {
        const verifiedEmail = await authService.verifyPasswordResetOobCode(oobCode);
        if (isMounted) {
          setResetEmail(verifiedEmail);
        }
      } catch (error: any) {
        if (!isMounted) {
          return;
        }
        const errorCode = String(error?.code || 'unknown');
        setCodeError(mapResetActionCodeError(errorCode));
        if (isTerminalResetCodeError(errorCode)) {
          onTerminalResult?.();
        }
      } finally {
        if (isMounted) {
          setCodeLoading(false);
        }
      }
    };

    void verifyCode();
    return () => {
      isMounted = false;
    };
  }, [oobCode, onTerminalResult]);

  useEffect(() => {
    if (!codeLoading) {
      stateHeadingRef.current?.focus();
    }
  }, [codeError, codeLoading, submitSuccess]);

  const handleSetNewPassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitError(null);

    if (!passwordValidation.isValid) {
      setSubmitError('Hasło nie spełnia wymagań bezpieczeństwa.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setSubmitError('Hasła nie są zgodne.');
      return;
    }

    setSubmitLoading(true);
    try {
      await authService.confirmPasswordResetWithCode(oobCode, newPassword);
      setSubmitSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      onTerminalResult?.();
    } catch (error: any) {
      const errorCode = String(error?.code || 'unknown');
      setSubmitError(mapResetActionCodeError(errorCode));
      if (isTerminalResetCodeError(errorCode)) {
        onTerminalResult?.();
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  if (codeLoading) {
    return (
      <AuthActionShell
        heading="Sprawdzamy link resetu"
        description="Potwierdzamy, czy link nadal pozwala bezpiecznie ustawić nowe hasło."
        statusProps={{ 'aria-live': 'polite' }}
      >
        <div className="flex min-h-28 items-center gap-3 text-sm text-primary-light-300">
          <Spinner size="sm" />
          Weryfikowanie linku resetu...
        </div>
      </AuthActionShell>
    );
  }

  if (codeError) {
    return (
      <AuthActionShell
        headingRef={stateHeadingRef}
        heading="Nie można użyć tego linku"
        description="Reset hasła nie został wykonany."
      >
        <div role="alert">
          <Alert variant="error" icon={<ExclamationCircleIcon className="h-5 w-5" />}>
            {codeError}
          </Alert>
        </div>
        <Link
          to={resetRequestTarget}
          className="mt-5 flex min-h-[46px] w-full items-center justify-center rounded-lg bg-ratuj-400 px-5 py-2.5 text-base font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300 focus-outline"
        >
          Poproś o nowy link resetu
        </Link>
        <Link
          to={loginTarget}
          className="mt-4 flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
        >
          Wróć do logowania
        </Link>
      </AuthActionShell>
    );
  }

  return (
    <AuthActionShell
      headingRef={stateHeadingRef}
      heading={submitSuccess ? 'Hasło zostało zmienione' : 'Ustaw nowe hasło'}
      description={
        submitSuccess
          ? 'Możesz teraz zalogować się przy użyciu nowego hasła.'
          : 'Ustaw nowe hasło dla swojego konta.'
      }
      statusProps={submitSuccess ? { 'aria-live': 'polite' } : undefined}
    >
      <form onSubmit={handleSetNewPassword} className="space-y-5">
        {resetEmail && !submitSuccess && (
          <Alert variant="info">Resetujesz hasło dla konta: {resetEmail}</Alert>
        )}
        {submitError && (
          <div role="alert">
            <Alert variant="error" icon={<ExclamationCircleIcon className="h-5 w-5" />}>
              {submitError}
            </Alert>
          </div>
        )}
        {submitSuccess && <Alert variant="success">Nowe hasło jest już aktywne.</Alert>}

        {!submitSuccess && (
          <>
            <PasswordInput
              id="newPassword"
              label="Nowe hasło"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="Minimum 8 znaków"
              icon={<LockClosedIcon className="h-5 w-5 text-primary-light-400/80" />}
              disabled={submitLoading}
            />
            <PasswordPolicyChecklist password={newPassword} validation={passwordValidation} />
            <PasswordInput
              id="confirmNewPassword"
              label="Potwierdź nowe hasło"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              placeholder="Powtórz nowe hasło"
              icon={<LockClosedIcon className="h-5 w-5 text-primary-light-400/80" />}
              disabled={submitLoading}
              error={isPasswordMismatch ? 'Hasła nie są zgodne.' : undefined}
            />

            <Button
              type="submit"
              variant="primary"
              size="lg"
              fullWidth
              disabled={submitLoading || !canSubmitNewPassword}
            >
              {submitLoading ? (
                <>
                  <Spinner size="sm" color="white" className="mr-2" />
                  Zapisywanie...
                </>
              ) : (
                'Ustaw nowe hasło'
              )}
            </Button>
          </>
        )}

        {submitSuccess ? (
          <Link
            to={loginTarget}
            className="flex min-h-[46px] w-full items-center justify-center rounded-lg bg-ratuj-400 px-5 py-2.5 text-base font-semibold text-white shadow-button transition-colors hover:bg-ratuj-300 focus-outline"
          >
            Zaloguj się
          </Link>
        ) : (
          <Link
            to={loginTarget}
            className="flex min-h-11 items-center justify-center rounded-lg text-sm text-ratuj-300 hover:text-ratuj-200 focus-outline"
          >
            Wróć do logowania
          </Link>
        )}
      </form>
    </AuthActionShell>
  );
};

export default ResetPasswordAction;
